import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  inject,
  computed,
  signal,
} from '@angular/core'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { DrawerComponent } from '@progress/kendo-angular-layout'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { filter, switchMap, takeUntil, tap, take } from 'rxjs/operators'

import {
  Enums,
  Hunt,
  HuntTemplate,
  SavedQuery,
  ExpandedHuntPackageRecord,
  HUNT_MAX,
} from '@cyborg-monorepo/hunter/common'
import { DialogRef, DialogService, SnackbarService } from '@hyperkit'

import { SessionService } from '@hunt/services'

import { NewHuntDialogComponent } from '../../shared/components/dialogs/new-hunt-dialog/new-hunt-dialog.component'
import { CustomHuntPackageService } from '../../shared/services/custom-hunt-package.service'
import { HuntTemplateService } from '../../shared/services/hunt-template.service'
import { ArchiveTemplateDialogComponent } from '../overview/archive-template-dialog/archive-template-dialog.component'
import { HuntAPIService } from '../services/api/hunt-api.service'

import { EditTemplateDialogComponent } from './edit-template-dialog/edit-template-dialog.component'

@Component({
  standalone: false,
  templateUrl: './hunt-template.component.html',
  styleUrls: ['hunt-template.component.scss'],
})
export class HuntTemplateComponent implements OnInit, OnDestroy {
  private dialogService = inject(DialogService)
  private route = inject(ActivatedRoute)
  private huntTemplateService = inject(HuntTemplateService)
  private router = inject(Router)
  private huntService = inject(HuntAPIService)
  private customHuntPackageService = inject(CustomHuntPackageService)
  private cdr = inject(ChangeDetectorRef)

  private readonly session = inject(SessionService)
  public readonly snack = inject(SnackbarService)

  @ViewChild('drawer') public drawer: DrawerComponent
  public huntTemplate$: Observable<HuntTemplate>
  public isDrawerExpanded = false
  public priority = Enums.PriorityEnum
  public huntDisabled = false
  public drawerWidth = 1000
  private destroy$ = new Subject<void>()
  public tooltip = signal<string>('')

  // Expose Enums to the template
  public Enums = Enums

  // BehaviorSubjects for filtered hunt packages
  public customerHuntPackages$ = new BehaviorSubject<
    ExpandedHuntPackageRecord[]
  >([])
  public nonCustomerHuntPackages$ = new BehaviorSubject<
    ExpandedHuntPackageRecord[]
  >([])

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.isDrawerExpanded = this.router.url.includes('custom-hunt-package')
      })
  }

  public ngOnInit(): void {
    this.huntTemplate$ = this.loadHuntTemplate()

    // Update drawer state dynamically
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const drawerOutlet = this.route.snapshot.children.find(
        (child) => child.outlet === 'drawer',
      )
      this.isDrawerExpanded = !!drawerOutlet
      this.cdr.detectChanges()
    })

    this.customHuntPackageService.refreshList$
      .pipe(
        takeUntil(this.destroy$),
        filter((shouldRefresh) => shouldRefresh),
      )
      .subscribe(() => {
        this.huntTemplate$ = this.loadHuntTemplate() // Reload data
      })

    this.checkInitialDrawerState()
  }

  private loadHuntTemplate(): Observable<HuntTemplate> {
    return this.huntTemplateService
      .getHuntTemplateById(this.route.snapshot.params['huntTemplateId'])
      .pipe(
        tap((result) => {
          this.huntDisabled =
            (result.hunts && result.hunts.length >= HUNT_MAX) ||
            result.huntPackages.length === 0

          if (result.huntPackages.length === 0)
            this.tooltip.set(
              `At least one hunt package is needed to create a hunt.`,
            )
          if (result.hunts && result.hunts.length >= HUNT_MAX)
            this.tooltip.set(
              `You have reached the maximum number of hunts (${HUNT_MAX}) for this template.`,
            )

          if (result?.huntPackages) {
            // Filter customer hunt packages
            const customerHuntPackages = result.huntPackages.filter(
              (huntPackage) =>
                huntPackage.source === this.Enums.SourceEnum.CUSTOMER,
            )
            this.customerHuntPackages$.next(customerHuntPackages)

            // Filter non-customer hunt packages
            const nonCustomerHuntPackages = result.huntPackages.filter(
              (huntPackage) =>
                huntPackage.source !== this.Enums.SourceEnum.CUSTOMER,
            )
            this.nonCustomerHuntPackages$.next(nonCustomerHuntPackages)
          } else {
            this.customerHuntPackages$.next([])
            this.nonCustomerHuntPackages$.next([])
          }
        }),
      )
  }

  private checkInitialDrawerState(): void {
    const drawerOutlet = this.route.snapshot.children.find(
      (child) => child.outlet === 'drawer',
    )
    if (drawerOutlet) {
      const contentId = drawerOutlet.children[0]?.params['contentId']
      this.isDrawerExpanded = true
      if (contentId) this.openExistingCustomContent(contentId)
      else this.openNewCustomContent()
    }
  }

  public toggleHtmlOverflow(isOpen: boolean): void {
    document.documentElement.style.overflow = isOpen ? 'hidden' : 'auto'
  }

  public onClose(UUID: string): void {
    this.huntTemplateService
      .removeHuntPackagesFromTemplate(
        this.route.snapshot.params['huntTemplateId'],
        [UUID],
      )
      .subscribe(() => {
        this.huntTemplate$ = this.loadHuntTemplate() // Refresh data
        this.snack.show('Hunt Package Removed', 5000)
      })
  }

  public openNewCustomContent(): void {
    this.toggleHtmlOverflow(true)
    this.router.navigate(
      [{ outlets: { drawer: ['custom-hunt-package', 'new'] } }],
      {
        relativeTo: this.route,
      },
    )
  }

  public openExistingCustomContent(contentId: string | undefined): void {
    if (!contentId) {
      console.error('Invalid UUID. Cannot navigate.')
      return
    }

    this.toggleHtmlOverflow(true)

    // Navigate using the 'drawer' outlet
    this.router.navigate(
      [{ outlets: { drawer: ['custom-hunt-package', contentId] } }],
      { relativeTo: this.route },
    )
  }

  public onCollapse(): void {
    this.toggleHtmlOverflow(false)
    this.router.navigate([
      '/hunt/hunt-template',
      this.route.snapshot.params['huntTemplateId'],
    ])
  }

  public editTemplate(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      EditTemplateDialogComponent,
      { data: this.route.snapshot.params['huntTemplateId'] },
    )

    dialogRef.afterClosed().subscribe((updatedTemplate: HuntTemplate) => {
      if (updatedTemplate) this.huntTemplate$ = this.loadHuntTemplate()
    })
  }

  public newHunt(): void {
    // Subscribe to huntTemplate$ to get the latest data
    this.huntTemplate$
      .pipe(take(1)) // Automatically completes after the first emission
      .subscribe((huntTemplate) => {
        // Open the dialog with resolved hunt template data
        const dialogRef: DialogRef = this.dialogService.open(
          NewHuntDialogComponent,
          {
            data: {
              huntTemplateId: this.route.snapshot.params['huntTemplateId'],
              huntPackages: huntTemplate.huntPackages, // Pass hunt packages to the dialog
              huntTemplate: huntTemplate, // Pass the entire hunt template
            },
          },
        )

        // Handle dialog close
        dialogRef.afterClosed().subscribe((hunt: Hunt) => {
          if (hunt) {
            this.router.navigate(['hunt', hunt.id, 'workbench'])
          }
        })
      })
  }

  public unarchiveHunt(hunt: Hunt): void {
    this.huntService
      .unarchiveHunt(hunt.id)
      .pipe(
        tap(async (returnedHunt: Hunt) => {
          this.session.markDirty()
          this.snack.show(`Removed ${returnedHunt.name} from archive`, 5000)
        }),
        switchMap(() => {
          this.huntTemplate$ = this.loadHuntTemplate()
          return this.huntTemplate$
        }),
      )
      .subscribe()
  }

  public deleteSavedQuery(query: SavedQuery): void {
    this.huntTemplateService
      .deleteSavedQuery(this.route.snapshot.params['huntTemplateId'], query)
      .subscribe(() => {
        this.huntTemplate$ = this.loadHuntTemplate()
      })
  }

  public archiveTemplate(): void {
    const templateId = this.route.snapshot.params['huntTemplateId']

    // Fetch the full HuntTemplate object
    this.huntTemplateService.getHuntTemplateById(templateId).subscribe({
      next: (huntTemplate: HuntTemplate) => {
        // Open the dialog with the HuntTemplate object as data
        const dialogRef: DialogRef = this.dialogService.open(
          ArchiveTemplateDialogComponent,
          { data: huntTemplate }, // Pass the full HuntTemplate object
        )

        // Handle dialog closure
        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            this.router.navigate(['/hunt/'])
            this.snack.show('Archived template', 5000)
          } else {
            console.warn('Archiving was canceled or failed.')
          }
        })
      },
      error: (err) => {
        console.error('Error fetching Hunt Template:', err)
        this.snack.show('Failed to fetch template for archiving.', 5000)
      },
    })
  }

  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  public getRouterLink(hunt: any): string[] {
    return ['/hunt', hunt.id]
  }
}
